'use client'

import { ReactNode } from 'react'

import { signIn } from 'next-auth/react'
import { useForm, FormProvider, type Resolver } from 'react-hook-form'
import { z } from 'zod'

import { zodResolver } from '@hookform/resolvers/zod'
import * as Sentry from '@sentry/react'

import { env } from '@/env'
import { useAuthContext } from '@/features/auth/contexts/AuthContext'
import { getErrorMessage } from '@/shared/lib/error'
import { isFunction } from '@/shared/lib/object'
import { cn } from '@/shared/lib/utils'

import { validationSchema } from './Fields/validationSchema'

const isDev = env.NEXT_PUBLIC_PGEE_APP_ENV === 'development'

type SignUpFormProps = {
  className?: string
  onSuccess?: () => void | Promise<void>
  beforeConfirmation?: (data: unknown) => void | Promise<void>
  children?: ReactNode
}
type FormData = z.input<typeof validationSchema>

const generateFieldValues = () => {
  const email = `wendell+${(Math.random() + 1)
    .toString(36)
    .substring(7)}@tootz.com.br`

  return {
    name: 'Wendell Adriel',
    email,
    email_confirmation: email,
    password: '123123123',
    password_confirmation: '123123123',
    birthdate: '2000-01-01'
  }
}

export function SignUpForm({
  className = '',
  onSuccess,
  beforeConfirmation,
  children
}: SignUpFormProps) {
  const {
    httpClient,
    goToConfirmation,
    isLoading,
    setIsLoading,
    setCurrentEmail,
    callbackUrl,
    config
  } = useAuthContext()
  const skipConfirmation = !!config?.signUp?.skipConfirmation

  const methods = useForm<FormData>({
    resolver: zodResolver(validationSchema) as unknown as Resolver<FormData>,
    defaultValues: (isDev ? generateFieldValues() : {}) as FormData
  })

  const handleSignUp = async (data: FormData) => {
    if (isLoading) return

    setIsLoading(true)

    try {
      const { name, email, password, password_confirmation } = data

      setCurrentEmail(email)

      await httpClient.post('/auth/sign-up', {
        record: {
          ...data,
          name,
          email,
          password,
          password_confirmation
        }
      })

      if (skipConfirmation) {
        await signIn('domain-sign-in', {
          login: email,
          password: password,
          callbackUrl: callbackUrl ?? '/'
        })
      } else {
        if (isFunction(beforeConfirmation)) {
          await beforeConfirmation({ ...data, email, password })
        }
        goToConfirmation({ email, password })
      }
    } catch (error) {
      Sentry.captureException(error)

      const { message } = getErrorMessage(error)

      methods.setError('email', {
        type: 'custom',
        message
      })
    }

    setIsLoading(false)
  }

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(handleSignUp)}
        className={cn('grid gap-6', className)}
        autoComplete="off"
      >
        {children}
      </form>
    </FormProvider>
  )
}
